import { useState } from 'react';
import {
  Control,
  Controller,
  FieldPathByValue,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { CircleXIcon } from 'lucide-react';

import { AIHintProps } from 'components/AIHint';
import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import { Input } from 'components/input';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  ExtendedFormContextType,
  useExtendedFormContext,
} from 'contexts/extendedFormContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import type { LoadDateValue } from 'types/Load';
import {
  FieldAttributes,
  getFieldAttribute,
  initFieldAttributes,
} from 'types/LoadAttributes';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS, TMSTimezoneConfig } from 'types/enums/Integrations';
import { processTimeAndUpdateDate } from 'utils/processTimeAndUpdateDate';
import { cn } from 'utils/shadcn';

export type DateTimeInputProps<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, Maybe<LoadDateValue>>,
> = Omit<React.ComponentPropsWithoutRef<typeof RHFTextInput>, 'options'> &
  Omit<AIHintProps, 'name'> & {
    control: Control<TFieldValues>;
    name: TPath;
    label: string;
    required?: boolean;
    hideAIHint?: boolean;
    preventNormalizedLabelTZ?: boolean;
    hideTimePicker?: boolean;
    containerClassName?: string;
    timezone?: Undef<string>;
    options?: Omit<
      RegisterOptions<TFieldValues, TPath>,
      'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >;
    disabled?: boolean;
  };

export default function DateTimeInput<
  TFieldValues extends FieldValues,
  TPath extends FieldPathByValue<TFieldValues, Maybe<LoadDateValue>>,
>({
  control,
  name,
  label,
  required,
  hideAIHint,
  preventNormalizedLabelTZ,
  hideTimePicker = false,
  containerClassName,
  timezone,
  options,
  disabled,
}: DateTimeInputProps<TFieldValues, TPath>) {
  const { tmsName } = useTMSContext();
  const isTZAgnosticTMS =
    TMSTimezoneConfig[tmsName as TMS]?.isLoadTimezoneAgnostic ?? true;
  const shortTZ = isTZAgnosticTMS ? '' : `(${dayjs().tz().format('z')})`;
  const normalizedLabel =
    preventNormalizedLabelTZ || isTZAgnosticTMS ? label : label + ' ' + shortTZ;
  const [timeInput, setTimeInput] = useState('');

  const formContext = useFormContext();
  const {
    setError,
    clearErrors,
    formState: { dirtyFields, errors },
  } = formContext;

  let extendedFormContext: Maybe<ExtendedFormContextType> = null;
  try {
    extendedFormContext = useExtendedFormContext();
  } catch {
    // No extended form context available
  }
  const highlightDirtyField =
    !!extendedFormContext &&
    (() => {
      const highlightDirtyFields =
        extendedFormContext.highlightDirtyFields ?? false;
      const fieldState = formContext.getFieldState(name, formContext.formState);
      const isDirty = fieldState.isDirty || get(dirtyFields, name);

      return Boolean(isDirty && highlightDirtyFields);
    })();

  const allFieldAttrs = useFieldAttributes();
  const thisFieldAttr: FieldAttributes =
    getFieldAttribute(allFieldAttrs, name) ?? initFieldAttributes;

  const invalidTimeMessage = 'Invalid time format. Please use HH:mm';

  const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeInput(e.target.value);
  };

  const handleTimeInputBlur = (field: any) => {
    if (!timeInput.trim()) {
      const fieldError = get(errors, name);
      if (
        fieldError?.type === 'manual' &&
        fieldError?.message === invalidTimeMessage
      ) {
        clearErrors(name);
      }
      return;
    }

    const newDate = processTimeAndUpdateDate(timeInput, field.value, timezone);
    if (newDate) {
      clearErrors(name);
      field.onChange(newDate);
      setTimeInput('');
    } else {
      setError(name, {
        type: 'manual',
        message: invalidTimeMessage,
      });
    }
  };

  return thisFieldAttr.isNotSupported ? null : (
    <Flex direction='col' gap='xs' className={containerClassName}>
      <Label name={name} hideAIHint={hideAIHint} required={required}>
        {normalizedLabel}
      </Label>
      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? 'Required' : undefined,
          ...options,
        }}
        render={({ field }) => {
          let displayTime = '';
          if (field.value && !hideTimePicker) {
            const dateTime = dayjs(field.value);
            displayTime = timezone
              ? dateTime.tz(timezone).format('HH:mm')
              : dateTime.format('HH:mm');
          }

          return (
            <Flex gap='xs' className='mt-1 w-full min-w-0' data-name={name}>
              <Flex gap='lg' className='min-w-0 flex-1'>
                <div
                  className={cn(
                    'min-w-0',
                    hideTimePicker ? 'w-full' : 'flex-1'
                  )}
                >
                  <DatePicker
                    field={field}
                    thisFieldAttr={thisFieldAttr}
                    highlightDirtyField={highlightDirtyField}
                    className='w-full'
                    timezone={timezone}
                  />
                </div>
                {!hideTimePicker && (
                  <Input
                    type='text'
                    className={cn(
                      'h-8 w-20 shrink-0 rounded-sm border border-neutral-input-border bg-neutral-input-bg text-neutral-input-text pr-1! pl-3',
                      highlightDirtyField && 'bg-warning-200'
                    )}
                    placeholder='HH:mm'
                    value={timeInput || displayTime}
                    name={name}
                    disabled={thisFieldAttr.isReadOnly || disabled}
                    readOnly={thisFieldAttr.isReadOnly || disabled}
                    onChange={handleTimeInputChange}
                    onBlur={() => handleTimeInputBlur(field)}
                  />
                )}
              </Flex>
              {field.value && (
                <button
                  title='Clear date'
                  onClick={() => field.onChange(null)}
                  className='flex h-9 shrink-0 items-center justify-center'
                >
                  <CircleXIcon className='h-4 w-4' />
                </button>
              )}
            </Flex>
          );
        }}
      />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }: { message: string }) => (
          <Typography variant='body-xs' className='text-error-500'>
            {message}
          </Typography>
        )}
      />
    </Flex>
  );
}
